import { Button, Input, Modal, Notification, Select, FileDrop, Loader } from '@scuf/common';
import useFetch from 'helpers/hooks/useFetch';
import { Report } from 'models/maintenanceReport/allMaintenances';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { MAINTENANCES_REPORTS, SPARE_PARTS } from 'resources';
import { modelState } from 'resources/atoms/maintenanceReportAtom';
import 'styles/maintenanceReport.scss';
import Alert from 'components/alert';
import { LabelDescription } from 'components';
import { downloadFile } from 'resources/api/';
import { downloadPublicFile } from 'resources/api/general_api/PublicFiles';

function NewMaintenanceReportAddItemsVisit() {
    const { t } = useTranslation(['maintenancereport', 'common']);
    const [productOptions, setProductOptions] = useState<any>();
    const [problemDescription, setProblemDescription] = useState<string>();
    const [model, setModel] = useRecoilState<Report>(modelState);
    const [uploadXLSX, setUploadXLSX] = useState<boolean>(false);
    const [arquivoXLSX, setArquivoXLSX] = useState<File | undefined>();
    const [uploadXLSXResult, setUploadXLSXResult] = useState<string>('');
    const [uploadXLSXSuccess, setUploadXLSXSuccess] = useState<boolean>(true);
    const [fullLoading, setFullLoading] = useState<string>('');

    const [products, getOptions, isLoading] = useFetch<never>({
        url: SPARE_PARTS.getOptions,
        onError: 'LoadInfo'
    });

    useEffect(() => {
        handleGetProducts();
    }, []);

    const handleGetProducts = async () => {
        if (!products?.data || products?.data.length === 0) {
            await getOptions(undefined, resp => {
                setProductOptions(
                    resp.data?.map((e: any, index: number) => ({
                        value: index,
                        text: e.name
                    }))
                );
            });
        } else {
            setProductOptions(
                products?.data?.map((e: any, index: number) => ({
                    value: index,
                    text: e.name
                }))
            );
        }
    };

    const addProduct = () => {
        const newItem = [
            {
                ProblemDescription: problemDescription,
                Product: model?.ProductName,
                SerialNumber: model?.SerialNumber
            }
        ];
        setModel(prevModel => ({
            ...prevModel,
            ReportItems: [...(prevModel.ReportItems ?? []), ...newItem]
        }));
        setProblemDescription('');
    };

    const postXLSX = async () => {
        if (!arquivoXLSX) {
            setUploadXLSXResult(t('messages.noFileSelected'));
            setUploadXLSXSuccess(false);
            return;
        }

        setFullLoading(t('messages.importingFile'));
        setUploadXLSX(false);

        const formData = new FormData();
        formData.append('file', arquivoXLSX);
        formData.append('reportId', model?.Id?.toString() || '0');

        try {
            const response = await fetch(`${MAINTENANCES_REPORTS.uploadItemsExcel}`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                setUploadXLSXSuccess(false);
                setUploadXLSXResult(data.message || t('messages.importError'));
            } else {
                let msg = t('messages.uploadSuccess', { successCount: data.successCount });

                if (data.failCount > 0) {
                    msg +=
                        ' ' +
                        t('messages.uploadPartialFail', {
                            failCount: data.failCount,
                            failedProducts: data.failedProducts.join(', ')
                        });
                }

                setUploadXLSXSuccess(true);
                setUploadXLSXResult(msg);

                if (data.items && Array.isArray(data.items)) {
                    setModel(prev => ({
                        ...prev,
                        ReportItems: [...(prev.ReportItems ?? []), ...data.items]
                    }));
                }
            }
        } catch (error) {
            console.error(error);
            setUploadXLSXSuccess(false);
            setUploadXLSXResult(t('messages.sendError'));
        } finally {
            setFullLoading('');
            setArquivoXLSX(undefined);
        }
    };

    const renderModal = () => {
        return (
            <>
                {/* Modal Upload Planilha */}
                <Modal
                    closeIcon={true}
                    className="popup-theme-wrap"
                    onClose={() => setUploadXLSX(false)}
                    open={uploadXLSX}
                    size="small"
                >
                    <Modal.Header>{t('repaircenter:translation.UploadSpreadsheet')}</Modal.Header>
                    <Modal.Content>
                        <Alert text={t('repaircenter:translation.UploadXLSXT1')} />
                        <div
                            className="no-padding blue-text"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => downloadPublicFile('maintenance-template', 'TemplateHoneywell.xlsx')}
                        >
                            <LabelDescription title="TemplateHoneywell.xlsx" />
                        </div>
                        <FileDrop
                            acceptedTypes={['.xlsx']}
                            resetButtonText={t('common:actions.Clean')}
                            multiple={false}
                            buttonText={t('common:actions.BrowseFiles')}
                            label={t('common:translation.File').toUpperCase()}
                            placeholder={t('common:actions.Draganddropfileshere')}
                            onAccepted={(file: File[]) => setArquivoXLSX(file[0])}
                        />
                    </Modal.Content>
                    <Modal.Footer>
                        <Button
                            type="secondary"
                            size="small"
                            content={t('common:actions.Cancel')}
                            onClick={() => setUploadXLSX(false)}
                        />
                        <Button type="primary" size="small" content="Ok" onClick={() => postXLSX()} />
                    </Modal.Footer>
                </Modal>

                {/* Modal Resultado Upload */}
                <Modal
                    className="popup-theme-wrap"
                    onClose={() => setUploadXLSXResult('')}
                    open={uploadXLSXResult !== ''}
                    size="small"
                >
                    <Modal.Header>{t('repaircenter:translation.UploadSpreadsheet')}</Modal.Header>
                    <Modal.Content>
                        <Notification hasIcon={true} severity={uploadXLSXSuccess ? 'success' : 'critical'}>
                            {uploadXLSXResult}
                        </Notification>
                    </Modal.Content>
                    <Modal.Footer>
                        <Button type="primary" size="small" content="Ok" onClick={() => setUploadXLSXResult('')} />
                    </Modal.Footer>
                </Modal>

                {/* Loader */}
                <Modal className="popup-theme-wrap" open={fullLoading !== ''} size="small">
                    <Modal.Content>
                        <Loader overlayColor="#202020" text={fullLoading} inverted />
                    </Modal.Content>
                </Modal>
            </>
        );
    };

    return (
        <div>
            {renderModal()}
            <div className="form3Columns">
                <Select
                    indicator={'required'}
                    label={t('common:translation.Product')}
                    placeholder={isLoading ? t('common:actions.Loading') : t('common:translation.Product')}
                    search={true}
                    disabled={isLoading}
                    options={productOptions ?? []}
                    value={model?.ProductName && productOptions?.findIndex((x: any) => x.text == model?.ProductName)}
                    onChange={e => setModel({ ...model, ProductName: productOptions[e].text })}
                />
                <Input
                    className="serial"
                    label={t('common:translation.SerialNumber')}
                    placeholder={t('common:translation.SerialNumber')}
                    indicator="required"
                    value={model?.SerialNumber || ''}
                    onChange={(value: string) => setModel({ ...model, SerialNumber: value })}
                />
                <Input
                    className="problem"
                    indicator={'required'}
                    placeholder={t('common:translation.Problem')}
                    label={t('common:translation.Problem')}
                    value={problemDescription ?? ''}
                    onChange={(value: string) => setProblemDescription(value)}
                />
            </div>
            <div className="buttonsSearch">
                <Button
                    className="button-add"
                    type="primary"
                    icon="slidercontrols-plus"
                    content={t('common:actions.Add')}
                    iconPosition="right"
                    disabled={!problemDescription || !model?.SerialNumber || !model?.ProductName}
                    onClick={() => addProduct()}
                />
                <Button
                    type="primary"
                    icon="slidercontrols-plus"
                    content={t('common:actions.XLSXUpload')}
                    iconPosition="right"
                    onClick={() => setUploadXLSX(true)}
                />
            </div>
        </div>
    );
}

export default NewMaintenanceReportAddItemsVisit;
